import { cn } from '../../lib/utils';
import { useStreamingText } from '../../hooks/useStreamingText';

/**
 * Small pulsing dot for use inside buttons as a loading indicator.
 * Inherits color from the parent via `currentColor`.
 */
type InlinePulsingDotProps = {
  /** Outer container size class (e.g. "h-4 w-4", "h-3.5 w-3.5") */
  size?: string;
  /** Tailwind color classes for the ping and dot layers.
   *  Defaults to `currentColor`-based opacity. */
  pingColor?: string;
  dotColor?: string;
};

export function InlinePulsingDot({
  size = 'h-4 w-4',
  pingColor = 'bg-current/40',
  dotColor = 'bg-current/60',
}: InlinePulsingDotProps): JSX.Element {
  // Inner dot is 75% of outer — we derive smaller classes from size string.
  // For common sizes: 4->3, 3.5->2.5
  const innerSize = size
    .replace(/h-4\b/, 'h-3')
    .replace(/w-4\b/, 'w-3')
    .replace(/h-3\.5\b/, 'h-2.5')
    .replace(/w-3\.5\b/, 'w-2.5');

  return (
    <span className={cn('relative flex items-center justify-center', size)}>
      <span className={cn('absolute inline-flex h-full w-full animate-ping rounded-full', pingColor)} />
      <span className={cn('relative inline-flex rounded-full', innerSize, dotColor)} />
    </span>
  );
}

type PulsingDotProps = {
  className?: string;
  isPulsing?: boolean;
  /** Optional status text to display next to the dot (streams in with typewriter effect).
   * Can be a single string or an array of strings for rotation on long loads. */
  statusText?: string | string[] | null;
};

export function PulsingDot({
  className,
  isPulsing = true,
  statusText,
}: PulsingDotProps): JSX.Element {
  const { displayedText, isStreaming } = useStreamingText(statusText ?? null);

  return (
    <div className={cn('flex items-center gap-3 py-3', className)}>
      <span className="relative flex h-[18px] w-[18px] items-center justify-center shrink-0">
        {isPulsing && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/60" />
        )}
        <span className="relative inline-flex h-[15px] w-[15px] rounded-full bg-white/70" />
      </span>
      {(displayedText || isStreaming) && (
        <span className="text-white/50 text-sm">
          {displayedText}
          {isStreaming && (
            <span className="inline-block w-[2px] h-[1em] bg-white/50 ml-0.5 animate-pulse align-middle" />
          )}
        </span>
      )}
    </div>
  );
}
